import React, { useState, useEffect } from 'react';

const BUSINESS_MODELS = [
  'Market Rate Apartment',
  'Short Term Rental',
  'Workforce Housing',
  'Sell',
];

const UNIT_TYPES = [
  { key: 'studio', label: 'Studio' },
  { key: 'oneBR', label: '1 Bedroom' },
  { key: 'twoBR', label: '2 Bedroom' },
];

function normalizeSliders(values) {
  const total = Object.values(values).reduce((a, b) => a + b, 0);
  if (total === 0) return values;
  const normalized = {};
  Object.entries(values).forEach(([k, v]) => {
    normalized[k] = Math.round((v / total) * 100);
  });
  const diff = 100 - Object.values(normalized).reduce((a, b) => a + b, 0);
  if (diff !== 0) {
    const firstKey = Object.keys(normalized)[0];
    normalized[firstKey] += diff;
  }
  return normalized;
}

function getDefaultBuildingState() {
  return {
    selectedBusinessModels: [],
    businessModelDist: BUSINESS_MODELS.reduce((acc, m) => ({ ...acc, [m]: 0 }), {}),
    unitMix: BUSINESS_MODELS.reduce((acc, m) => ({
      ...acc,
      [m]: UNIT_TYPES.reduce((a, t) => ({ ...a, [t.key]: 0 }), {})
    }), {})
  };
}

const UnitMixPage = ({ buildPaths = [], boxes = [] }) => {
  // Ensure buildPaths and boxes are arrays
  const safeBuildPaths = Array.isArray(buildPaths) ? buildPaths : [];
  const safeBoxes = Array.isArray(boxes) ? boxes : [];
  
  // Build real building list from buildPaths
  const buildings = safeBuildPaths.map(bp => ({
    id: bp.id,
    name: `Building ${bp.id}`,
    boxes: safeBoxes.filter(box => box.buildPathId === bp.id).map(box => box.id)
  }));

  // Fallback to empty if no buildings
  const initialBuildingId = buildings.length > 0 ? buildings[0].id : null;
  const [selectedBuildingId, setSelectedBuildingId] = useState(initialBuildingId);
  useEffect(() => {
    if (buildings.length > 0 && !buildings.find(b => b.id === selectedBuildingId)) {
      setSelectedBuildingId(buildings[0].id);
    }
  }, [buildings, selectedBuildingId]);

  // Per-building state
  const [buildingSettings, setBuildingSettings] = useState({});
  useEffect(() => {
    // Initialize state for new buildings
    setBuildingSettings(prev => {
      const next = { ...prev };
      buildings.forEach(b => {
        if (!next[b.id]) next[b.id] = getDefaultBuildingState();
      });
      // Remove state for buildings that no longer exist
      Object.keys(next).forEach(id => {
        if (!buildings.find(b => b.id === Number(id))) delete next[id];
      });
      return next;
    });
  }, [buildings]);

  const currentSettings = (selectedBuildingId && buildingSettings[selectedBuildingId]) ? buildingSettings[selectedBuildingId] : getDefaultBuildingState();
  const building = buildings.find((b) => b.id === selectedBuildingId) || { boxes: [] };
  const totalUnits = building.boxes.length * 2;

  if (!building || !currentSettings || buildings.length === 0) {
    return <div style={{ padding: 32 }}>No buildings available. Please create a layout first.</div>;
  }

  // Handlers for per-building state
  const handleBusinessModelChange = (model) => {
    setBuildingSettings(prev => {
      const settings = { ...prev[selectedBuildingId] };
      const prevSelected = settings.selectedBusinessModels;
      const next = prevSelected.includes(model)
        ? prevSelected.filter((m) => m !== model)
        : [...prevSelected, model];
      // If adding, set equal distribution
      if (!prevSelected.includes(model)) {
        const newDist = { ...settings.businessModelDist, [model]: 100 / (next.length) };
        next.forEach((m) => {
          if (m !== model) newDist[m] = (settings.businessModelDist[m] * prevSelected.length) / next.length;
        });
        settings.businessModelDist = normalizeSliders(newDist);
      } else {
        // If removing, re-normalize
        const filtered = Object.fromEntries(Object.entries(settings.businessModelDist).filter(([k]) => next.includes(k)));
        settings.businessModelDist = normalizeSliders(filtered);
      }
      settings.selectedBusinessModels = next;
      return { ...prev, [selectedBuildingId]: settings };
    });
  };

  const handleBusinessModelSlider = (model, value) => {
    setBuildingSettings(prev => {
      const settings = { ...prev[selectedBuildingId] };
      const updated = { ...settings.businessModelDist, [model]: value };
      settings.businessModelDist = normalizeSliders(updated);
      return { ...prev, [selectedBuildingId]: settings };
    });
  };

  const handleUnitMixSlider = (model, type, value) => {
    setBuildingSettings(prev => {
      const settings = { ...prev[selectedBuildingId] };
      const updated = { ...settings.unitMix[model], [type]: value };
      settings.unitMix = { ...settings.unitMix, [model]: normalizeSliders(updated) };
      return { ...prev, [selectedBuildingId]: settings };
    });
  };

  return (
    <div style={{ minHeight: '100vh', width: '100vw', background: '#f8fafc', padding: 0, margin: 0 }}>
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 32px 0 32px' }}>
        <h2 style={{ marginBottom: 32 }}>Unit Mix & Business Model</h2>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 40 }}>
          {/* Building Selector */}
          <div style={{ minWidth: 160 }}>
            <h3 style={{ fontSize: 18, marginBottom: 16 }}>Buildings</h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {buildings.map((b) => (
                <li key={b.id}>
                  <button
                    style={{
                      padding: '12px 20px',
                      marginBottom: 12,
                      background: b.id === selectedBuildingId ? '#3B82F6' : '#fff',
                      color: b.id === selectedBuildingId ? 'white' : '#222',
                      border: b.id === selectedBuildingId ? '2px solid #2563eb' : '1px solid #e5e7eb',
                      borderRadius: 6,
                      cursor: 'pointer',
                      width: '100%',
                      fontWeight: 600,
                      fontSize: 16,
                      boxShadow: b.id === selectedBuildingId ? '0 2px 8px rgba(59,130,246,0.08)' : 'none',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => setSelectedBuildingId(b.id)}
                  >
                    {b.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {/* Main Config Panel */}
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: 18, marginBottom: 12 }}>Business Model Distribution</h3>
            {currentSettings.selectedBusinessModels.length === 0 && <div style={{ color: '#888', marginBottom: 16 }}>Select at least one business model.</div>}
            <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
              {BUSINESS_MODELS.map((model) => (
                <label key={model} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 15 }}>
                  <input
                    type="checkbox"
                    checked={currentSettings.selectedBusinessModels.includes(model)}
                    onChange={() => handleBusinessModelChange(model)}
                  />
                  {model}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 32, marginBottom: 12 }}>
              {currentSettings.selectedBusinessModels.map((model) => (
                <div key={model} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 180 }}>
                  <label style={{ fontWeight: 500, marginBottom: 4 }}>{model}</label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={currentSettings.businessModelDist[model] || 0}
                    onChange={(e) => handleBusinessModelSlider(model, Number(e.target.value))}
                    style={{ width: 140, margin: '0 0 4px 0' }}
                    disabled={currentSettings.selectedBusinessModels.length === 1}
                  />
                  <span style={{ minWidth: 32, display: 'inline-block', fontWeight: 600 }}>{currentSettings.businessModelDist[model] || 0}%</span>
                </div>
              ))}
            </div>
            {/* Unit Mix Columns */}
            {currentSettings.selectedBusinessModels.length > 0 && (
              <div style={{ display: 'flex', gap: 32, marginBottom: 28 }}>
                {currentSettings.selectedBusinessModels.map((model) => {
                  const businessModelUnits = Math.round(totalUnits * (currentSettings.businessModelDist[model] / 100));
                  return (
                    <div key={model} style={{ flex: 1, background: '#f1f5f9', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.03)', padding: 18, minWidth: 180 }}>
                      <h4 style={{ margin: '0 0 16px 0', fontWeight: 700, fontSize: 16, textAlign: 'center' }}>{model} Unit Mix</h4>
                      {UNIT_TYPES.map((type) => {
                        const unitCount = Math.round(businessModelUnits * (currentSettings.unitMix[model][type.key] / 100));
                        return (
                          <div key={type.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 18 }}>
                            <span style={{ fontWeight: 600, fontSize: 18, marginBottom: 2 }}>{unitCount}</span>
                            <label style={{ minWidth: 80, display: 'inline-block', fontWeight: 500 }}>{type.label}</label>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              value={currentSettings.unitMix[model][type.key] || 0}
                              onChange={(e) => handleUnitMixSlider(model, type.key, Number(e.target.value))}
                              style={{ width: 100, margin: '0 0 4px 0' }}
                            />
                            <span style={{ minWidth: 32, display: 'inline-block', fontWeight: 600 }}>{currentSettings.unitMix[model][type.key] || 0}%</span>
                          </div>
                        );
                      })}
                      <div style={{ textAlign: 'center', color: '#888', fontSize: 13, marginTop: 8 }}>
                        Total: {businessModelUnits} units
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <h3 style={{ fontSize: 18, margin: '32px 0 12px 0' }}>Box/Bay Assignment (Schematic)</h3>
            <table style={{ borderCollapse: 'collapse', width: '100%', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '12px 8px', fontWeight: 700, fontSize: 15 }}>Box</th>
                  <th style={{ padding: '12px 8px', fontWeight: 700, fontSize: 15 }}>Left Bay</th>
                  <th style={{ padding: '12px 8px', fontWeight: 700, fontSize: 15 }}>Hallway</th>
                  <th style={{ padding: '12px 8px', fontWeight: 700, fontSize: 15 }}>Right Bay</th>
                </tr>
              </thead>
              <tbody>
                {building.boxes.map((boxId) => (
                  <tr key={boxId}>
                    <td style={{ padding: '10px 8px', fontWeight: 600 }}>Box {boxId}</td>
                    <td style={{ background: '#e0f2fe', padding: '10px 8px' }}>[unit type]</td>
                    <td style={{ background: '#f3f4f6', padding: '10px 8px' }}>Hallway</td>
                    <td style={{ background: '#e0f2fe', padding: '10px 8px' }}>[unit type]</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 32, textAlign: 'right' }}>
              <button style={{ padding: '12px 32px', background: '#22c55e', color: 'white', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 17, boxShadow: '0 2px 8px rgba(34,197,94,0.08)' }}>
                Export Assignments
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitMixPage; 